/**
 * Created: 2026-04-07
 * Initial commit: Add court category and create-event dialog (feat/event/mr-10)
 *
 * MUI-compatible numeric input using react-number-format.
 * Adapted from tm/client/src/widget/NumberInput.tsx.
 */

import { useCallback } from 'react'
import type { NumberFormatValues, NumericFormatProps } from 'react-number-format'
import { NumericFormat } from 'react-number-format'

export interface NumberInputChangeEvent {
  target: {
    value: number | null
  }
}

interface NumberInputProps extends Omit<NumericFormatProps, 'onChange'> {
  onChange: (event: NumberInputChangeEvent) => void
  max?: number
  ref?: React.RefObject<HTMLInputElement>
}

/**
 * A wrapper around <NumericFormat> that provides some sensible defaults and
 * adds the `max` prop. Designed to be used as a custom MUI inputComponent.
 */
export default function NumberInput(props: NumberInputProps) {
  const {
    onChange,
    isAllowed,
    max,
    thousandSeparator = false,
    fixedDecimalScale = true,
    allowNegative = false,
    allowLeadingZeros = false,
    decimalScale = 2,
    defaultValue,
    ref,
    ...other
  } = props

  const _isAllowed = useCallback(
    (values: NumberFormatValues) => {
      if (
        (isAllowed && !isAllowed(values)) ||
        (max !== undefined &&
          values.floatValue !== undefined &&
          values.floatValue > max)
      )
        return false
      return true
    },
    [isAllowed, max],
  )

  const onValueChange = useCallback(
    (values: NumberFormatValues) => {
      onChange({
        target: {
          value:
            values.floatValue ??
            (typeof defaultValue === 'number' ? defaultValue : null),
        },
      })
    },
    [onChange, defaultValue],
  )

  return (
    <NumericFormat
      getInputRef={ref}
      onValueChange={onValueChange}
      thousandSeparator={thousandSeparator}
      allowNegative={allowNegative}
      allowLeadingZeros={allowLeadingZeros}
      decimalScale={decimalScale}
      isAllowed={_isAllowed}
      fixedDecimalScale={fixedDecimalScale}
      {...other}
    />
  )
}
